import Button from "components/common/Button";
import SectionWrapper from "components/common/SectionWrapper";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { techs } from "store/allData";

const stats = [
  { value: "20+", label: "Products shipped" },
  { value: "7", label: "Senior specialists" },
  { value: "10", label: "Years building" },
  { value: "4", label: "Disciplines, one team" },
];

const marqueeTechs = [
  techs.react,
  techs.next,
  techs.typescript,
  techs.golang,
  techs.python,
  techs.solidity,
  techs.solana,
  techs.openai,
  techs.langgraph,
  techs.tailwindcss,
  techs.psql,
  techs.docker,
  techs.nodejs,
  techs.graphql,
  techs.figma,
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero: React.FC = () => {
  const router = useRouter();
  return (
    <section className="relative w-full overflow-hidden pt-40 lg:pt-48 pb-10">
      {/* background layers */}
      <div className="absolute inset-0 bg-grid" aria-hidden="true" />
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(126,92,255,0.22) 0%, rgba(126,92,255,0.06) 45%, transparent 70%)",
        }}
        aria-hidden="true"
      />

      <SectionWrapper className="relative z-10 mx-auto flex flex-col items-center text-center">
        <motion.div
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-zinc-300"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          Available for new projects
        </motion.div>

        <motion.h1
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mt-8 max-w-4xl font-heading font-bold tracking-tight text-4xl ssm:text-5xl md:text-6xl lg:text-7xl !leading-[1.08] text-white"
        >
          We build digital products{" "}
          <span className="text-gradient">people love to use</span>
        </motion.h1>

        <motion.p
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mt-6 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed"
        >
          Spila is a digital product studio of software engineers, AI
          specialists, designers, and blockchain developers — turning ambitious
          ideas into AI, software, and on-chain products.
        </motion.p>

        <motion.div
          custom={3}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mt-10 flex flex-wrap items-center justify-center gap-3"
        >
          <Button
            size={2}
            variant="primary"
            clickHandler={() => router.push("#contact-us")}
            className="font-semibold font-heading"
          >
            Start a project
          </Button>
          <Button
            size={2}
            variant="outline"
            clickHandler={() => router.push("#projects")}
            className="font-semibold font-heading"
          >
            See our work
          </Button>
        </motion.div>

        <motion.dl
          custom={4}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mt-16 lg:mt-24 grid grid-cols-2 md:grid-cols-4 gap-px w-full max-w-3xl rounded-2xl border border-white/10 bg-white/10 overflow-hidden"
        >
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="flex flex-col items-center justify-center gap-1 bg-[#0B0B11] py-6 px-4"
            >
              <dd className="font-heading text-2xl md:text-3xl font-bold text-white order-1">
                {stat.value}
              </dd>
              <dt className="text-xs text-zinc-500 order-2">{stat.label}</dt>
            </div>
          ))}
        </motion.dl>
      </SectionWrapper>

      {/* tech marquee */}
      <motion.div
        custom={5}
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        className="relative z-10 mt-16 w-full overflow-hidden"
        style={{
          maskImage:
            "linear-gradient(to right, transparent, black 15%, black 85%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent, black 15%, black 85%, transparent)",
        }}
      >
        <div className="flex w-max animate-marquee gap-3 pr-3">
          {[...marqueeTechs, ...marqueeTechs].map((tech, index) => (
            <span
              key={tech.name + index}
              className="whitespace-nowrap rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 text-xs font-medium text-zinc-500"
            >
              {tech.name}
            </span>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
